if (!customElements.get('fomo-block')) {
  class FomoBlock extends HTMLElement {
    constructor() {
      super();
      this.initElements();
      this.initState();
      this.initObserver();
      this.initInterval();
      this.checkAndInitialize();
    }

    // Initialize and cache relevant DOM elements
    initElements() {
      this.fomoCount = this.querySelector('#fomo-count');
      this.intervalId = null;
      this.isInitialized = false;
      this.initializedKey = `fomo-is-initialized-${window.location.hostname}`;

      // Read from localStorage
      const isInitialized = localStorage.getItem(this.initializedKey) === 'true';

      // Set up interval once globally, not for each element
      if (!isInitialized) {
        localStorage.setItem(this.initializedKey, 'true');

        // Clear any existing interval
        if (window.fomoIntervalId) {
          clearInterval(window.fomoIntervalId);
          window.fomoIntervalId = null;
        }
      }
    }

    // Initialize component state
    initState() {
      this.min = parseInt(this.dataset.min || 5, 10);
      this.max = parseInt(this.dataset.max || 50, 10);
      this.interval = parseInt(this.dataset.interval || 10, 10) * 1000;
      this.storageKey = 'fomo-counter-value';
    }

    // Initialize MutationObserver to watch for DOM changes
    initObserver() {
      this.observer = new MutationObserver((mutations) => {
        let shouldReinit = false;
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // Element node
              // Check if the added node is the fomo-count element or contains it
              if (node.id === 'fomo-count') {
                this.initializeCounter(node);
                shouldReinit = true;
              } else if (node.querySelector) {
                const fomoCount = node.querySelector('#fomo-count');
                if (fomoCount) {
                  this.initializeCounter(fomoCount);
                  shouldReinit = true;
                }
              }
            }
          });

          // Also check for class changes that might indicate active media change
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const target = mutation.target;
            if (target.classList && target.classList.contains('product__media-item')) {
              if (target.classList.contains('is-active')) {
                shouldReinit = true;
              }
            }
          }
        });

        // Reinitialize if needed to ensure visible counter is set
        if (shouldReinit) {
          setTimeout(() => {
            const visibleCounter = this.findVisibleFomoCounter();
            if (visibleCounter) {
              const current = this.getCurrentValue();
              visibleCounter.textContent = current;
            }
          }, 100);
        }
      });

      // Start observing the document body for changes
      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // Set up update interval after state is initialized
    initInterval() {
      const isInitialized = localStorage.getItem(this.initializedKey) === 'true';

      if (isInitialized && !window.fomoIntervalId) {
        window.fomoIntervalId = setInterval(() => {
          // Update all fomo-block instances
          document.querySelectorAll('fomo-block').forEach(instance => {
            if (instance.updateCount) {
              instance.updateCount();
            }
          });
        }, this.interval);
      }
    }

    // Get or initialize counter value from sessionStorage
    getCurrentValue() {
      const stored = sessionStorage.getItem(this.storageKey);
      if (stored !== null) {
        const val = parseInt(stored, 10);
        if (val >= this.min && val <= this.max) {
          return val;
        }
      }
      return Math.floor(Math.random() * (this.max - this.min + 1)) + this.min;
    }

    // Function to check and initialize counter
    checkAndInitialize() {
      const visibleCounter = this.findVisibleFomoCounter();
      if (visibleCounter) {
        this.initializeCounter(visibleCounter);
      }
    }

    // Save counter value to sessionStorage
    saveCurrentValue(value) {
      sessionStorage.setItem(this.storageKey, value.toString());
    }

    // Find the currently visible/active FOMO counter element
    findVisibleFomoCounter() {
      // Try to find the FOMO counter in the active/visible product media item
      const activeMediaItem = document.querySelector('.product__media-item.is-active');
      if (activeMediaItem) {
        const fomoCount = activeMediaItem.querySelector('#fomo-count');
        if (fomoCount && this.isElementVisible(fomoCount)) {
          return fomoCount;
        }
      }

      // Fallback: find any visible FOMO counter
      const allFomoCounters = document.querySelectorAll('#fomo-count');
      for (let i = 0; i < allFomoCounters.length; i++) {
        if (this.isElementVisible(allFomoCounters[i])) {
          return allFomoCounters[i];
        }
      }

      // Last resort: return first one found
      return document.querySelector('#fomo-count');
    }

    // Check if element is visible
    isElementVisible(element) {
      if (!element) return false;
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
        return false;
      }
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }

    // Update counter value
    updateCount() {
      const activeElement = this.findVisibleFomoCounter();
      if (activeElement) {
        let currentVal = parseInt(activeElement.textContent, 10);
        // If element exists but has no valid number, get from storage
        if (isNaN(currentVal)) {
          currentVal = this.getCurrentValue();
          activeElement.textContent = currentVal;
        }

        const change = Math.floor(Math.random() * 11) - 5; // -5 to +5
        let newVal = currentVal + change;

        if (newVal < this.min) newVal = this.min;
        if (newVal > this.max) newVal = this.max;

        activeElement.textContent = newVal;
        this.saveCurrentValue(newVal);
      }
    }

    // Initialize counter for a specific element
    initializeCounter(fomoCountElement) {
      if (!fomoCountElement) {
        return;
      }

      const current = this.getCurrentValue();
      fomoCountElement.textContent = current;
    }

    // Cleanup when component is disconnected
    disconnectedCallback() {
      // Check if there are any other fomo-block instances
      const remainingInstances = document.querySelectorAll('fomo-block');

      // If this was the last instance, clear the interval
      if (remainingInstances.length <= 1) {
        if (window.fomoIntervalId) {
          clearInterval(window.fomoIntervalId);
          window.fomoIntervalId = null;
          localStorage.removeItem(this.initializedKey);
        }
      }

      if (this.observer) {
        this.observer.disconnect();
      }
    }
  }

  customElements.define('fomo-block', FomoBlock);
}

